<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import {
  AD_SOURCE_KEY,
  duplicateKeysFor,
  isReservedDimensionKey,
  normalizeLostReasons,
  slugifySourceKey,
} from 'kanban/helper/analyticsSettings';

const props = defineProps({
  settings: {
    type: Object,
    default: () => ({}),
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save']);

const { t } = useI18n();
const store = useStore();
const accountLabels = useMapGetter('labels/getLabels');

onMounted(() => {
  if (!accountLabels.value?.length) store.dispatch('labels/get');
});

const dimensions = ref([]);
const sources = ref([]);
const rules = ref([]);
const defaultSource = ref('');
const adSourceName = ref('');
const lostReasons = ref([]);
// Once the form has been touched it owns the state: a funnel update arriving over realtime
// (someone flipping an automation toggle) must not wipe what is being typed here.
const isDirty = ref(false);

const loadFromSettings = () => {
  dimensions.value = (props.settings?.dimensions || []).map(dimension => ({
    key: dimension.key || slugifySourceKey(dimension.name || ''),
    name: dimension.name || dimension.key || '',
    labels: [...(dimension.labels || [])],
  }));
  sources.value = (props.settings?.sources || [])
    .filter(source => source.key !== AD_SOURCE_KEY)
    .map(source => ({
      key: source.key || slugifySourceKey(source.name || ''),
      name: source.name || source.key || '',
    }));
  adSourceName.value =
    (props.settings?.sources || []).find(source => source.key === AD_SOURCE_KEY)
      ?.name || '';
  rules.value = (props.settings?.source_rules || []).map(rule => ({
    match: rule.match || '',
    source: rule.source || '',
  }));
  defaultSource.value = props.settings?.default_source || '';
  lostReasons.value = (props.settings?.lost_reasons || []).map(name => ({
    name,
  }));
  isDirty.value = false;
};

watch(
  () => props.settings,
  () => {
    if (!isDirty.value) loadFromSettings();
  },
  { immediate: true, deep: true }
);

const touch = () => {
  isDirty.value = true;
};

const labelNames = computed(() =>
  (accountLabels.value || []).map(label => label.title)
);

// --- eixos ---------------------------------------------------------------------------

const addDimension = () => {
  dimensions.value.push({ key: '', name: '', labels: [] });
  touch();
};

const removeDimension = index => {
  dimensions.value.splice(index, 1);
  touch();
};

const toggleLabel = (dimension, label) => {
  const index = dimension.labels.indexOf(label);
  if (index >= 0) dimension.labels.splice(index, 1);
  else dimension.labels.push(label);
  touch();
};

// Selected labels float to the top so a long label list stays usable.
const orderedLabels = dimension => [
  ...dimension.labels.filter(label => labelNames.value.includes(label)),
  ...labelNames.value.filter(label => !dimension.labels.includes(label)),
];

// --- origens -------------------------------------------------------------------------

// The Meta ad origin is stamped by Atende itself, so it is always on the list and cannot be
// removed -- only renamed, so the report shows a human name instead of the stored key.
const adSource = computed(() => ({
  key: AD_SOURCE_KEY,
  name:
    adSourceName.value.trim() ||
    t('KANBAN.SETTINGS.ANALYTICS.SOURCES.AD_DEFAULT_NAME'),
}));

const addSource = () => {
  sources.value.push({ key: '', name: '' });
  touch();
};

const removeSource = index => {
  sources.value.splice(index, 1);
  touch();
};

// The stored key is derived from the name only while it is still blank, so renaming an
// origin never orphans the cards already stamped with it.
const namedSources = computed(() =>
  sources.value
    .filter(source => source.name.trim())
    .map(source => ({
      key: source.key || slugifySourceKey(source.name),
      name: source.name.trim(),
    }))
);

const allSources = computed(() => [adSource.value, ...namedSources.value]);

const sourceSelectOptions = computed(() =>
  allSources.value.map(source => ({ value: source.key, label: source.name }))
);

const defaultSourceOptions = computed(() => [
  { value: '', label: t('KANBAN.SETTINGS.ANALYTICS.RULES.DEFAULT_NONE') },
  ...sourceSelectOptions.value,
]);

// --- regras --------------------------------------------------------------------------

const addRule = () => {
  rules.value.push({ match: '', source: allSources.value[0]?.key || '' });
  touch();
};

const removeRule = index => {
  rules.value.splice(index, 1);
  touch();
};

// A rule pointing at an origin that was just removed would silently never fire.
const orphanRules = computed(() =>
  rules.value.filter(
    rule =>
      rule.match.trim() &&
      !allSources.value.some(source => source.key === rule.source)
  )
);

// --- motivos de perda ----------------------------------------------------------------

const addLostReason = () => {
  lostReasons.value.push({ name: '' });
  touch();
};

const removeLostReason = index => {
  lostReasons.value.splice(index, 1);
  touch();
};

// Reasons are stored as the words people read, so two entries differing only in casing
// would be one reason twice in the card's picker.
const duplicateLostReasons = computed(() =>
  duplicateKeysFor(
    lostReasons.value
      .filter(reason => reason.name.trim())
      .map(reason => ({ key: reason.name.trim().toLowerCase() }))
  )
);

const lostReasonError = reason =>
  duplicateLostReasons.value.has(reason.name.trim().toLowerCase())
    ? t('KANBAN.SETTINGS.ANALYTICS.LOST_REASONS.DUPLICATE')
    : '';

// --- validacao -----------------------------------------------------------------------

const namedDimensions = computed(() =>
  dimensions.value
    .filter(dimension => dimension.name.trim())
    .map(dimension => ({
      key: dimension.key || slugifySourceKey(dimension.name),
      name: dimension.name.trim(),
      labels: dimension.labels,
    }))
);

const duplicateDimensionKeys = computed(() =>
  duplicateKeysFor(namedDimensions.value)
);
const duplicateSourceKeys = computed(() => duplicateKeysFor(allSources.value));

const dimensionError = dimension => {
  if (!dimension.name.trim() && dimension.labels.length) {
    return t('KANBAN.SETTINGS.ANALYTICS.DIMENSIONS.ERROR_NAME');
  }
  if (dimension.name.trim() && !dimension.labels.length) {
    return t('KANBAN.SETTINGS.ANALYTICS.DIMENSIONS.ERROR_LABELS');
  }
  const key = String(
    dimension.key || slugifySourceKey(dimension.name)
  ).toLowerCase();
  if (isReservedDimensionKey(key)) {
    return t('KANBAN.SETTINGS.ANALYTICS.DIMENSIONS.ERROR_RESERVED_KEY');
  }
  if (key && duplicateDimensionKeys.value.has(key)) {
    return t('KANBAN.SETTINGS.ANALYTICS.DUPLICATE_KEY');
  }
  return '';
};

const sourceError = source => {
  const key = String(source.key || slugifySourceKey(source.name)).toLowerCase();
  return key && duplicateSourceKeys.value.has(key)
    ? t('KANBAN.SETTINGS.ANALYTICS.DUPLICATE_KEY')
    : '';
};

const hasErrors = computed(
  () =>
    duplicateDimensionKeys.value.size > 0 ||
    duplicateSourceKeys.value.size > 0 ||
    duplicateLostReasons.value.size > 0 ||
    orphanRules.value.length > 0 ||
    dimensions.value.some(dimension => dimensionError(dimension))
);

const onSave = () => {
  emit('save', {
    dimensions: namedDimensions.value.filter(
      dimension => dimension.labels.length
    ),
    sources: allSources.value,
    source_rules: rules.value
      .filter(
        rule =>
          rule.match.trim() &&
          allSources.value.some(source => source.key === rule.source)
      )
      .map(rule => ({ match: rule.match.trim(), source: rule.source })),
    default_source: allSources.value.some(
      source => source.key === defaultSource.value
    )
      ? defaultSource.value
      : '',
    lost_reasons: normalizeLostReasons(
      lostReasons.value.map(reason => reason.name)
    ),
  });
  isDirty.value = false;
};
</script>

<template>
  <div class="flex flex-col gap-8">
    <!-- Eixos de analise -->
    <section class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.SETTINGS.ANALYTICS.DIMENSIONS.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{ t('KANBAN.SETTINGS.ANALYTICS.DIMENSIONS.DESCRIPTION') }}
        </p>
      </div>

      <div
        v-for="(dimension, index) in dimensions"
        :key="`dimension-${index}`"
        class="flex flex-col gap-3 p-4 border rounded-lg border-n-weak"
      >
        <div class="flex items-start gap-2">
          <div class="flex-1 min-w-0">
            <Input
              v-model="dimension.name"
              :placeholder="
                t('KANBAN.SETTINGS.ANALYTICS.DIMENSIONS.NAME_PLACEHOLDER')
              "
              :message="dimensionError(dimension)"
              :message-type="dimensionError(dimension) ? 'error' : 'info'"
              @input="touch"
            />
          </div>
          <Button
            size="sm"
            variant="faded"
            color="ruby"
            icon="i-lucide-trash-2"
            @click="removeDimension(index)"
          />
        </div>

        <div class="flex flex-col gap-2">
          <span class="text-xs text-n-slate-11">
            {{
              t('KANBAN.SETTINGS.ANALYTICS.DIMENSIONS.SELECTED', {
                count: dimension.labels.length,
              })
            }}
          </span>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="label in orderedLabels(dimension)"
              :key="label"
              type="button"
              class="px-2 py-1 text-xs border rounded-full transition-colors"
              :class="
                dimension.labels.includes(label)
                  ? 'border-n-brand bg-n-brand/10 text-n-brand'
                  : 'border-n-weak text-n-slate-11 hover:text-n-slate-12'
              "
              @click="toggleLabel(dimension, label)"
            >
              {{ label }}
            </button>
            <p v-if="!labelNames.length" class="text-xs text-n-slate-11">
              {{ t('KANBAN.SETTINGS.ANALYTICS.DIMENSIONS.NO_LABELS') }}
            </p>
          </div>
        </div>
      </div>

      <div>
        <Button
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-plus"
          :label="t('KANBAN.SETTINGS.ANALYTICS.DIMENSIONS.ADD')"
          @click="addDimension"
        />
      </div>
    </section>

    <!-- Origens -->
    <section class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.SETTINGS.ANALYTICS.SOURCES.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{
            t('KANBAN.SETTINGS.ANALYTICS.SOURCES.DESCRIPTION', {
              name: adSource.name,
            })
          }}
        </p>
      </div>

      <div class="flex items-start gap-2">
        <div class="flex-1 min-w-0">
          <Input
            v-model="adSourceName"
            :placeholder="
              t('KANBAN.SETTINGS.ANALYTICS.SOURCES.AD_DEFAULT_NAME')
            "
            :message="t('KANBAN.SETTINGS.ANALYTICS.SOURCES.AD_HINT')"
            @input="touch"
          />
        </div>
        <span class="mt-2.5 text-xs text-n-slate-10 whitespace-nowrap">
          {{ AD_SOURCE_KEY }}
        </span>
      </div>

      <div
        v-for="(source, index) in sources"
        :key="`source-${index}`"
        class="flex items-start gap-2"
      >
        <div class="flex-1 min-w-0">
          <Input
            v-model="source.name"
            :placeholder="
              t('KANBAN.SETTINGS.ANALYTICS.SOURCES.NAME_PLACEHOLDER')
            "
            :message="sourceError(source)"
            :message-type="sourceError(source) ? 'error' : 'info'"
            @input="touch"
          />
        </div>
        <span
          v-if="source.key"
          class="mt-2.5 text-xs text-n-slate-10 whitespace-nowrap"
        >
          {{ source.key }}
        </span>
        <Button
          size="sm"
          variant="faded"
          color="ruby"
          icon="i-lucide-trash-2"
          @click="removeSource(index)"
        />
      </div>

      <div>
        <Button
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-plus"
          :label="t('KANBAN.SETTINGS.ANALYTICS.SOURCES.ADD')"
          @click="addSource"
        />
      </div>
    </section>

    <!-- Regras -->
    <section class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.SETTINGS.ANALYTICS.RULES.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{ t('KANBAN.SETTINGS.ANALYTICS.RULES.DESCRIPTION') }}
        </p>
      </div>

      <div
        v-for="(rule, index) in rules"
        :key="`rule-${index}`"
        class="flex flex-col gap-2 sm:flex-row sm:items-start"
      >
        <div class="flex-1 min-w-0">
          <Input
            v-model="rule.match"
            :placeholder="
              t('KANBAN.SETTINGS.ANALYTICS.RULES.MATCH_PLACEHOLDER')
            "
            @input="touch"
          />
        </div>
        <div class="w-full sm:w-56">
          <Select
            v-model="rule.source"
            :options="sourceSelectOptions"
            @update:model-value="touch"
          />
        </div>
        <Button
          size="sm"
          variant="faded"
          color="ruby"
          icon="i-lucide-trash-2"
          @click="removeRule(index)"
        />
      </div>

      <p v-if="orphanRules.length" class="text-xs text-n-ruby-11">
        {{ t('KANBAN.SETTINGS.ANALYTICS.RULES.ORPHAN') }}
      </p>

      <div>
        <Button
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-plus"
          :label="t('KANBAN.SETTINGS.ANALYTICS.RULES.ADD')"
          @click="addRule"
        />
      </div>

      <div class="flex flex-col gap-1 pt-2 max-w-sm">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('KANBAN.SETTINGS.ANALYTICS.RULES.DEFAULT') }}
        </label>
        <Select
          v-model="defaultSource"
          :options="defaultSourceOptions"
          @update:model-value="touch"
        />
      </div>
    </section>

    <!-- Motivos de perda -->
    <section class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('KANBAN.SETTINGS.ANALYTICS.LOST_REASONS.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-11">
          {{ t('KANBAN.SETTINGS.ANALYTICS.LOST_REASONS.DESCRIPTION') }}
        </p>
      </div>

      <div
        v-for="(reason, index) in lostReasons"
        :key="`lost-reason-${index}`"
        class="flex items-start gap-2"
      >
        <div class="flex-1 min-w-0">
          <Input
            v-model="reason.name"
            :placeholder="
              t('KANBAN.SETTINGS.ANALYTICS.LOST_REASONS.NAME_PLACEHOLDER')
            "
            maxlength="255"
            :message="lostReasonError(reason)"
            :message-type="lostReasonError(reason) ? 'error' : 'info'"
            @input="touch"
          />
        </div>
        <Button
          size="sm"
          variant="faded"
          color="ruby"
          icon="i-lucide-trash-2"
          @click="removeLostReason(index)"
        />
      </div>

      <div>
        <Button
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-plus"
          :label="t('KANBAN.SETTINGS.ANALYTICS.LOST_REASONS.ADD')"
          @click="addLostReason"
        />
      </div>
    </section>

    <div class="flex items-center gap-3">
      <Button
        size="sm"
        :is-loading="isSaving"
        :disabled="hasErrors || !isDirty"
        :label="t('KANBAN.SETTINGS.ANALYTICS.SAVE')"
        @click="onSave"
      />
      <span v-if="hasErrors" class="text-xs text-n-ruby-11">
        {{ t('KANBAN.SETTINGS.ANALYTICS.FIX_ERRORS') }}
      </span>
    </div>
  </div>
</template>
